/**
 * Settings Page
 * Unified workspace + project settings.
 * Workspace: Build Defaults, GitHub, Agent Config, AI Prompts, Document Types.
 * Project: General, Repository, Agent Overrides, Data & Tools.
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { SecondaryPanel } from '../components/layout/SecondaryPanel';
import { SettingsSideNav, type SettingsCategory } from '../components/settings/SettingsSideNav';
import { BuildDefaultsSettings } from '../components/settings/BuildDefaultsSettings';
import { GitHubIntegration } from '../components/settings/GitHubIntegration';
import { AppearanceSettings } from '../components/settings/AppearanceSettings';
import { AgentConfigSettings } from '../components/settings/AgentConfigSettings';
import { PromptsSettings } from '../components/settings/PromptsSettings';
import { DocumentTypeSettings } from '../components/settings/DocumentTypeSettings';
import { ProjectGeneralSettings } from '../components/settings/ProjectGeneralSettings';
import { ProjectRepositorySettings } from '../components/settings/ProjectRepositorySettings';
import { ProjectAgentOverrides } from '../components/settings/ProjectAgentOverrides';
import { ProjectDataTools } from '../components/settings/ProjectDataTools';
import { PluginRegistrySettings } from '../components/settings/PluginRegistrySettings';
import { MarketplaceSourcesSettings } from '../components/settings/MarketplaceSourcesSettings';
import { DeploymentsSettings } from '../components/settings/DeploymentsSettings';
import { AgentFleetSettings } from '../components/settings/AgentFleetSettings';
import { IntegrationsPage } from './settings/IntegrationsPage';
import { TokenSettings } from '../components/settings/TokenSettings';
import SkillsPage from '../panes/archie/pages/SkillsPage';
import { useProject } from '../contexts/ProjectContext';

// ─── Route → Category mapping ───────────────────────────────────

function categoryFromPath(pathname: string): SettingsCategory {
  if (pathname.endsWith('/github') || pathname.includes('/github/')) return 'github';
  if (pathname.endsWith('/appearance')) return 'appearance';
  if (pathname.endsWith('/agents') && !pathname.includes('/project/')) return 'agents';
  if (pathname.endsWith('/prompts')) return 'prompts';
  if (pathname.endsWith('/dt')) return 'doc-types';
  if (pathname.endsWith('/plugins')) return 'plugins';
  if (pathname.endsWith('/integrations') || pathname.endsWith('/ig')) return 'integrations';
  if (pathname.endsWith('/mcp')) return 'tokens';
  if (pathname.endsWith('/tokens')) return 'tokens';
  if (pathname.endsWith('/skills')) return 'skills';
  if (pathname.endsWith('/deployments')) return 'deployments';
  if (pathname.endsWith('/fleet')) return 'fleet';
  // Project categories
  if (pathname.includes('/project/repo')) return 'project-repo';
  if (pathname.includes('/project/agents')) return 'project-agents';
  if (pathname.includes('/project/tools')) return 'project-tools';
  if (pathname.includes('/project')) return 'project-general';
  return 'build';
}

function pathSegmentFromCategory(cat: SettingsCategory): string {
  switch (cat) {
    case 'github': return 'se/github';
    case 'appearance': return 'se/appearance';
    case 'agents': return 'se/agents';
    case 'prompts': return 'se/prompts';
    case 'doc-types': return 'se/dt';
    case 'plugins': return 'se/plugins';
    case 'integrations': return 'ig';
    case 'tokens': return 'se/tokens';
    case 'skills': return 'se/skills';
    case 'deployments': return 'se/deployments';
    case 'fleet': return 'se/fleet';
    case 'project-general': return 'se/project';
    case 'project-repo': return 'se/project/repo';
    case 'project-agents': return 'se/project/agents';
    case 'project-tools': return 'se/project/tools';
    default: return 'se';
  }
}

const TITLES: Record<SettingsCategory, string> = {
  'build': 'Build Defaults',
  'github': 'GitHub Integration',
  'appearance': 'Appearance',
  'agents': 'Agent Configuration',
  'prompts': 'AI Prompts',
  'doc-types': 'Document Types',
  'plugins': 'Plugins',
  'integrations': 'Integrations',
  'tokens': 'API Tokens',
  'skills': "Martha's Skills",
  'deployments': 'Deployments',
  'fleet': 'Agent Fleet',
  'project-general': 'Project Settings',
  'project-repo': 'Repository',
  'project-agents': 'Agent Overrides',
  'project-tools': 'Data & Tools',
};

const DESCRIPTIONS: Record<SettingsCategory, string> = {
  'build': 'Workspace-level defaults for branching, PR management, quality gates, and sprites.',
  'github': 'Connect your GitHub organization for repository access and PR management.',
  'appearance': 'Choose the platform provider — brand palette and platform sizing for the entire UI.',
  'agents': 'Configure code execution prompts, quality gate commands, and default verify checks.',
  'prompts': 'Customize AI prompt templates used across analysis, planning, and agent tasks.',
  'doc-types': 'Manage document type schemas and their properties.',
  'plugins': 'Reusable instruction sets injected into sprite build prompts.',
  'integrations': 'Connect external services like HubSpot, Figma, Slack, and custom MCP servers.',
  'tokens': 'Create and manage personal access tokens for MCP agent authentication.',
  'skills': 'Claude Code /commands served from Martha — install on any machine with Martha connected.',
  'deployments': 'Production deployment pipeline, approval gates, and history.',
  'fleet': 'Deployment agent trust score, autonomy tiers, and fleet management.',
  'project-general': 'Project name, type, status, and general configuration.',
  'project-repo': 'GitHub repository and branch configuration for this project.',
  'project-agents': 'Override workspace agent settings for this project.',
  'project-tools': 'Seed demo data or manage destructive project operations.',
};

// ─── Main Component ─────────────────────────────────────────────

export const SettingsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { wsSlug } = useParams<{ wsSlug: string }>();
  const { project } = useProject();

  const [activeCategory, setActiveCategory] = useState<SettingsCategory>(
    () => categoryFromPath(location.pathname)
  );
  const [activeSection, setActiveSection] = useState<string>();

  // Section refs for scroll-to
  const sectionRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  const handleCategoryChange = useCallback((cat: SettingsCategory) => {
    setActiveCategory(cat);
    setActiveSection(undefined);
    navigate(`/w/${wsSlug}/${pathSegmentFromCategory(cat)}`, { replace: true });
  }, [navigate]);

  const handleSectionClick = useCallback((section: string) => {
    setActiveSection(section);
    const ref = sectionRefs.current[section];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Lazy-create refs for sections
  const getSectionRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    const sections: Partial<Record<SettingsCategory, string[]>> = {
      'build': ['Branching', 'PR & Merge', 'Quality Gates', 'Integration Testing', 'Sprites'],
      'agents': ['Code Execution', 'Defaults', 'Sprite Instructions'],
      'prompts': ['Analysis', 'Planning'],
    };
    for (const sec of sections[activeCategory] || []) {
      refs[sec] = React.createRef<HTMLDivElement>();
    }
    sectionRefs.current = refs;
    return refs;
  }, [activeCategory]);

  const header = (
    <div className="bg-white border-b border-neutral-200 px-6 py-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center">
          <Settings2 size={16} className="text-neutral-500" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-neutral-800">
            {TITLES[activeCategory]}
          </h1>
          <p className="text-[12px] text-neutral-500">
            {DESCRIPTIONS[activeCategory]}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      header={header}
      stickyHeader
      secondaryPanel={
        <SecondaryPanel
          title="Settings"
          icon={<Settings2 size={16} />}
          storageKey="settings"
          width={260}
        >
          <SettingsSideNav
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
            activeSection={activeSection}
            onSectionClick={handleSectionClick}
            projectName={project?.name}
          />
        </SecondaryPanel>
      }
      contentBackground="#FAFAF8"
    >
      {activeCategory === 'skills' && <SkillsPage category="martha" />}
      <div className={`mx-auto p-6 ${['plugins', 'deployments', 'fleet', 'integrations', 'tokens'].includes(activeCategory) ? 'max-w-4xl' : 'max-w-2xl'} ${activeCategory === 'skills' ? 'hidden' : ''}`}>
        {/* Workspace categories */}
        {activeCategory === 'build' && (
          <BuildDefaultsSettings sectionRefs={getSectionRefs} />
        )}
        {activeCategory === 'github' && (
          <GitHubIntegration />
        )}
        {activeCategory === 'appearance' && (
          <AppearanceSettings />
        )}
        {activeCategory === 'agents' && (
          <AgentConfigSettings sectionRefs={getSectionRefs} />
        )}
        {activeCategory === 'prompts' && (
          <PromptsSettings sectionRefs={getSectionRefs} />
        )}
        {activeCategory === 'doc-types' && (
          <DocumentTypeSettings />
        )}
        {activeCategory === 'plugins' && (
          <>
            <PluginRegistrySettings />
            <div className="mt-6">
              <MarketplaceSourcesSettings />
            </div>
          </>
        )}
        {activeCategory === 'integrations' && (
          <IntegrationsPage />
        )}
        {activeCategory === 'tokens' && (
          <TokenSettings />
        )}
        {/* Operations categories */}
        {activeCategory === 'deployments' && (
          <DeploymentsSettings />
        )}
        {activeCategory === 'fleet' && (
          <AgentFleetSettings />
        )}
        {/* Project categories */}
        {activeCategory === 'project-general' && (
          <ProjectGeneralSettings />
        )}
        {activeCategory === 'project-repo' && (
          <ProjectRepositorySettings />
        )}
        {activeCategory === 'project-agents' && (
          <ProjectAgentOverrides />
        )}
        {activeCategory === 'project-tools' && (
          <ProjectDataTools />
        )}
      </div>
    </PageLayout>
  );
};

export default SettingsPage;
